import { useEffect, useState } from "react";
import { FileText, Download, Printer, Loader2, Wand2 } from "lucide-react";
import { api, getToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

export default function LetterStudio() {
  const { apiErr } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [templateKey, setTemplateKey] = useState("");
  const [consumerId, setConsumerId] = useState("");
  const [bureau, setBureau] = useState("");
  const [letter, setLetter] = useState(null);
  const [bodyText, setBodyText] = useState("");
  const [building, setBuilding] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/letters/templates").then(({ data }) => setTemplates(data.templates));
    api.get("/consumers").then(({ data }) => setConsumers(data));
  }, []);

  const selectedTemplate = templates.find((t) => t.key === templateKey);
  const bureauOptions = selectedTemplate?.targets || [];

  const build = async () => {
    if (!templateKey || !consumerId || !bureau) { toast.error("Select template, consumer and bureau"); return; }
    setBuilding(true);
    try {
      // pull consumer's disputes as fraudulent accounts context
      const { data: c } = await api.get(`/consumers/${consumerId}`);
      const accounts = (c.disputes || []).filter((d) => d.bureau === bureau || bureau === "Furnisher").map((d) => ({
        creditor: d.creditor, accountNumber: d.accountNumber, balance: d.amount, reason: (d.notes || "").replace("Fraud reason: ", "") || "Never Authorized",
      }));
      const { data } = await api.post("/letters/build", { template_key: templateKey, consumerId, bureau, accounts });
      setLetter(data);
      setBodyText(data.body_paragraphs.join("\n\n"));
      toast.success("Letter generated");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBuilding(false); }
  };

  const payload = () => ({
    subject: letter.subject, date: letter.date, recipient: letter.recipient, sender: letter.sender,
    body_paragraphs: bodyText.split("\n\n").map((s) => s.trim()).filter(Boolean),
    filename: `${(letter.template_label || "dispute").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`,
  });

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/letters/pdf`, payload(), {
        withCredentials: true,
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = payload().filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  const printLetter = () => window.print();

  const bodyParas = bodyText.split("\n\n").map((s) => s.trim()).filter(Boolean);

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="letter-studio-panel">
      {/* Controls */}
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6 space-y-4">
          <div>
            <label className="text-sm text-neutral-600 mb-1.5 block">Dispute template</label>
            <Select value={templateKey} onValueChange={(v) => { setTemplateKey(v); setBureau(""); }}>
              <SelectTrigger className="bg-[#F6EEEE] border-neutral-200" data-testid="letter-template-selector"><SelectValue placeholder="Choose template…" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-neutral-600 mb-1.5 block">Consumer</label>
            <Select value={consumerId} onValueChange={setConsumerId}>
              <SelectTrigger className="bg-[#F6EEEE] border-neutral-200" data-testid="letter-consumer-selector"><SelectValue placeholder="Choose consumer…" /></SelectTrigger>
              <SelectContent>{consumers.map((c) => <SelectItem key={c.id} value={c.id}>{c.fullName}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-neutral-600 mb-1.5 block">Recipient / bureau</label>
            <Select value={bureau} onValueChange={setBureau} disabled={!templateKey}>
              <SelectTrigger className="bg-[#F6EEEE] border-neutral-200" data-testid="letter-bureau-selector"><SelectValue placeholder="Choose recipient…" /></SelectTrigger>
              <SelectContent>{bureauOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={build} disabled={building} className="w-full rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="generate-letter-button">
            {building ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />} Generate Letter
          </Button>
        </div>

        {letter && (
          <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6 space-y-3">
            <div className="text-sm font-medium text-neutral-600">Edit body</div>
            <Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={12}
              className="bg-[#F6EEEE] border-neutral-200 font-mono text-xs leading-relaxed" data-testid="letter-body-editor" />
            <div className="flex gap-2">
              <Button onClick={downloadPdf} disabled={downloading} className="flex-1 rounded-full bg-[#15803D] hover:bg-[#1E9E4B] text-white font-semibold" data-testid="download-pdf-letter-button">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> PDF</>}
              </Button>
              <Button onClick={printLetter} variant="outline" className="flex-1 rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="print-letter-button">
                <Printer className="h-4 w-4 mr-1.5" /> Print
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="lg:col-span-3">
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/40 p-4 h-full">
          {!letter ? (
            <div className="h-full min-h-[400px] grid place-items-center text-neutral-400">
              <div className="text-center"><FileText className="h-12 w-12 mx-auto opacity-40" /><p className="mt-3 text-sm">Generate a letter to preview it here</p></div>
            </div>
          ) : (
            <div id="print-letter" className="bg-white text-black rounded-lg p-8 sm:p-10 shadow-2xl font-serif text-[13px] leading-relaxed" data-testid="letter-preview-pane" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
              <div className="text-[11px]">
                {letter.sender?.name && <div>{letter.sender.name}</div>}
                {letter.sender?.address && <div>{letter.sender.address}</div>}
                {letter.sender?.email && <div>{letter.sender.email}</div>}
                {letter.sender?.phone && <div>{letter.sender.phone}</div>}
              </div>
              <div className="mt-5 text-[11px]">{letter.date}</div>
              <div className="mt-5 text-[11px]">
                {letter.recipient.map((r, i) => <div key={i}>{r}</div>)}
              </div>
              <div className="mt-4 inline-block border border-black px-2 py-1 text-[10px] font-bold">CERTIFIED MAIL — RETURN RECEIPT REQUESTED</div>
              <div className="mt-4 font-bold text-[12px]">RE: {letter.subject}</div>
              <div className="mt-4 space-y-3 text-justify">
                {bodyParas.map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
              </div>
              <div className="mt-8 text-[11px]">
                <div>_______________________________</div>
                <div>{letter.sender?.name}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
